import { FC, useState } from "react";
import { Button, Col, Form, FormProps, Input, Row, Select } from "antd";
import { PreferredCommunicationMethod } from "~/utils/constants";
import { RecipientType } from "~/types/communication";
import { useCreateCommunication } from "~/services/communication";
import { useNotification } from "~/hooks";
import { useDetailBookingStore } from "../../detail-booking.store";
import { useGetTemplates } from "~/services/template";
import { capitalize } from "~/utils";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking";
import { RecipientSelector } from "./recipient-selector";
import { SendEmailConfirmationModal, EmailRecipient } from "./send-email-confirmation-modal";

type Props = {};

export const SendNotification: FC<Props> = () => {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const { booking } = useDetailBookingStore();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationRecipients, setConfirmationRecipients] = useState<EmailRecipient[]>([]);
  const [confirmationSubject, setConfirmationSubject] = useState("");
  const [pendingValues, setPendingValues] = useState<any>(null);

  const { data: templates, isLoading } = useGetTemplates();
  const mutation = useCreateCommunication();

  // Handle template selection - auto-populate subject and body
  const handleTemplateChange = (templateId: string | undefined) => {
    if (!templateId) {
      // Template cleared - don't clear fields, user might have edited them
      return;
    }

    const selectedTemplate = templates?.items.find((t) => t.id === templateId);
    if (selectedTemplate) {
      // Auto-populate subject and body from template
      form.setFieldsValue({
        subject: selectedTemplate.subject || form.getFieldValue('subject'),
        body: selectedTemplate.body || form.getFieldValue('body'),
      });
    }
  };

  // Helper function to resolve recipient types and IDs to email addresses
  const resolveRecipients = (values: any) => {
    if (!booking) return [];

    const recipients: { email: string; name: string; role?: string }[] = [];

    // Handle TO recipients
    if (values.recipients) {
      const { recipientTypes, recipientIds, additionalEmails } = values.recipients;

      // Resolve recipient types
      if (recipientTypes?.includes(RecipientType.BOOKING_CLIENT)) {
        if (booking.client?.email) {
          recipients.push({
            email: booking.client.email,
            name: booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`,
            role: 'Booking Client',
          });
        }
      }

      if (recipientTypes?.includes(RecipientType.SERVICE_CLIENTS)) {
        booking.serviceTicket?.forEach((service: any) => {
          if (service.client?.email && !recipients.find(r => r.email === service.client.email)) {
            recipients.push({
              email: service.client.email,
              name: service.client.companyName || `${service.client.firstName} ${service.client.lastName}`,
              role: 'Service Client',
            });
          }
        });
      }

      if (recipientTypes?.includes(RecipientType.ASSIGNED_STAFF)) {
        booking.serviceTicket?.forEach((service: any) => {
          service.staffs?.forEach((staff: any) => {
            if (staff.user?.email && !recipients.find(r => r.email === staff.user.email)) {
              recipients.push({
                email: staff.user.email,
                name: `${staff.user?.firstName || ''} ${staff.user?.lastName || ''}`.trim() || 'Unknown Staff',
                role: 'Assigned Staff',
              });
            }
          });
        });
      }

      // Resolve specific recipient IDs (clients and staff)
      if (recipientIds?.length > 0) {
        // Check in booking client
        if (booking.client && recipientIds.includes(booking.client.id) && booking.client.email) {
          if (!recipients.find(r => r.email === booking.client.email)) {
            recipients.push({
              email: booking.client.email,
              name: booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`,
              role: 'Client',
            });
          }
        }

        // Check in service clients
        booking.serviceTicket?.forEach((service: any) => {
          if (service.client && recipientIds.includes(service.client.id) && service.client.email) {
            if (!recipients.find(r => r.email === service.client.email)) {
              recipients.push({
                email: service.client.email,
                name: service.client.companyName || `${service.client.firstName} ${service.client.lastName}`,
                role: 'Client',
              });
            }
          }

          // Check in service staff
          service.staffs?.forEach((staff: any) => {
            if (recipientIds.includes(staff.id) && staff.user?.email) {
              if (!recipients.find(r => r.email === staff.user.email)) {
                recipients.push({
                  email: staff.user.email,
                  name: `${staff.user?.firstName || ''} ${staff.user?.lastName || ''}`.trim() || 'Unknown Staff',
                  role: 'Staff',
                });
              }
            }
          });
        });
      }

      // Add additional emails
      if (additionalEmails?.length > 0) {
        additionalEmails.forEach((email: string) => {
          if (!recipients.find(r => r.email === email)) {
            recipients.push({
              email,
              name: email,
              role: 'Additional',
            });
          }
        });
      }
    }

    return recipients;
  };

  // Helper to resolve a RecipientSelection to recipients with names (for confirmation display)
  const resolveRecipientsWithNames = (recipientSelection: any, role: string): { email: string; name: string; role: string; isClient?: boolean }[] => {
    if (!recipientSelection || !booking) return [];

    const result: { email: string; name: string; role: string; isClient?: boolean }[] = [];
    const { recipientTypes, recipientIds, additionalEmails } = recipientSelection;

    // Resolve recipient types
    if (recipientTypes?.includes(RecipientType.BOOKING_CLIENT)) {
      if (booking.client?.email) {
        result.push({
          email: booking.client.email,
          name: booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`,
          role,
          isClient: true,
        });
      }
    }

    if (recipientTypes?.includes(RecipientType.SERVICE_CLIENTS)) {
      booking.serviceTicket?.forEach((service: any) => {
        if (service.client?.email && !result.find(r => r.email === service.client.email)) {
          result.push({
            email: service.client.email,
            name: service.client.companyName || `${service.client.firstName} ${service.client.lastName}`,
            role,
            isClient: true,
          });
        }
      });
    }

    if (recipientTypes?.includes(RecipientType.ASSIGNED_STAFF)) {
      booking.serviceTicket?.forEach((service: any) => {
        service.staffs?.forEach((staff: any) => {
          if (staff.user?.email && !result.find(r => r.email === staff.user.email)) {
            result.push({
              email: staff.user.email,
              name: `${staff.user?.firstName || ''} ${staff.user?.lastName || ''}`.trim() || 'Unknown Staff',
              role,
              isClient: false,
            });
          }
        });
      });
    }

    // Resolve specific recipient IDs
    if (recipientIds?.length > 0) {
      // Check booking client
      if (booking.client && recipientIds.includes(booking.client.id) && booking.client.email) {
        if (!result.find(r => r.email === booking.client.email)) {
          result.push({
            email: booking.client.email,
            name: booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`,
            role,
            isClient: true,
          });
        }
      }

      // Check service clients and staff
      booking.serviceTicket?.forEach((service: any) => {
        if (service.client && recipientIds.includes(service.client.id) && service.client.email) {
          if (!result.find(r => r.email === service.client.email)) {
            result.push({
              email: service.client.email,
              name: service.client.companyName || `${service.client.firstName} ${service.client.lastName}`,
              role,
              isClient: true,
            });
          }
        }

        service.staffs?.forEach((staff: any) => {
          if (recipientIds.includes(staff.id) && staff.user?.email) {
            if (!result.find(r => r.email === staff.user.email)) {
              result.push({
                email: staff.user.email,
                name: `${staff.user?.firstName || ''} ${staff.user?.lastName || ''}`.trim() || 'Unknown Staff',
                role,
                isClient: false,
              });
            }
          }
        });
      });
    }

    // Add additional emails
    if (additionalEmails?.length > 0) {
      additionalEmails.forEach((email: string) => {
        if (!result.find(r => r.email === email)) {
          result.push({
            email,
            name: email,
            role,
            isClient: false,
          });
        }
      });
    }

    return result;
  };

  const onSubmit: FormProps["onFinish"] = (values) => {
    if (!booking) return;

    // Resolve TO recipients
    const toRecipients = resolveRecipients(values);

    if (toRecipients.length === 0) {
      error({
        message: "Error",
        description: "No recipients selected. Please add at least one recipient.",
      });
      return;
    }

    // Resolve CC and BCC recipients for confirmation display
    const ccRecipientsResolved = resolveRecipientsWithNames(values.ccRecipients, 'CC');
    const bccRecipientsResolved = resolveRecipientsWithNames(values.bccRecipients, 'BCC');

    // Combine all for confirmation modal
    const allRecipients = [...toRecipients, ...ccRecipientsResolved, ...bccRecipientsResolved];

    // Show confirmation modal
    setConfirmationRecipients(allRecipients);
    setConfirmationSubject(values.subject);
    setPendingValues(values);
    setConfirmationOpen(true);
  };

  // Helper to resolve a RecipientSelection to just email addresses
  const resolveRecipientsToEmails = (recipientSelection: any): string[] => {
    if (!recipientSelection || !booking) return [];

    const emails: string[] = [];
    const { recipientTypes, recipientIds, additionalEmails } = recipientSelection;

    // Resolve recipient types
    if (recipientTypes?.includes(RecipientType.BOOKING_CLIENT)) {
      if (booking.client?.email) {
        emails.push(booking.client.email);
      }
    }

    if (recipientTypes?.includes(RecipientType.SERVICE_CLIENTS)) {
      booking.serviceTicket?.forEach((service: any) => {
        if (service.client?.email && !emails.includes(service.client.email)) {
          emails.push(service.client.email);
        }
      });
    }

    if (recipientTypes?.includes(RecipientType.ASSIGNED_STAFF)) {
      booking.serviceTicket?.forEach((service: any) => {
        service.staffs?.forEach((staff: any) => {
          if (staff.user?.email && !emails.includes(staff.user.email)) {
            emails.push(staff.user.email);
          }
        });
      });
    }

    // Resolve specific recipient IDs
    if (recipientIds?.length > 0) {
      // Check booking client
      if (booking.client && recipientIds.includes(booking.client.id) && booking.client.email) {
        if (!emails.includes(booking.client.email)) {
          emails.push(booking.client.email);
        }
      }

      // Check service clients and staff
      booking.serviceTicket?.forEach((service: any) => {
        if (service.client && recipientIds.includes(service.client.id) && service.client.email) {
          if (!emails.includes(service.client.email)) {
            emails.push(service.client.email);
          }
        }

        service.staffs?.forEach((staff: any) => {
          if (recipientIds.includes(staff.id) && staff.user?.email) {
            if (!emails.includes(staff.user.email)) {
              emails.push(staff.user.email);
            }
          }
        });
      });
    }

    // Add additional emails
    if (additionalEmails?.length > 0) {
      additionalEmails.forEach((email: string) => {
        if (!emails.includes(email)) {
          emails.push(email);
        }
      });
    }

    return emails;
  };

  const handleConfirmSend = () => {
    if (!booking || !pendingValues) return;

    const values = pendingValues;

    // Resolve CC and BCC recipients to email addresses
    const ccEmails = resolveRecipientsToEmails(values.ccRecipients);
    const bccEmails = resolveRecipientsToEmails(values.bccRecipients);

    // Flatten recipient data for API
    const payload = {
      name: values.subject || 'Communication',
      subject: values.subject,
      body: values.body,
      templateId: values.templateId,
      jobTicketId: booking.id,
      channels: PreferredCommunicationMethod.EMAIL,
      recipientTypes: values.recipients?.recipientTypes,
      recipientIds: values.recipients?.recipientIds,
      additionalEmails: values.recipients?.additionalEmails,
      cc: ccEmails.length > 0 ? ccEmails : undefined,
      bcc: bccEmails.length > 0 ? bccEmails : undefined,
    };

    mutation.mutate(
      payload,
      {
        onSuccess: () => {
          success({
            message: "Success",
            description: "Email sent successfully.",
          });
          qc.invalidateQueries({
            queryKey: BOOKING_KEYS.GET_BOOKING(booking.id),
          });
          form.resetFields();
          setConfirmationOpen(false);
          setPendingValues(null);
        },
        onError: (err) => {
          error({
            message: "Error",
            description: err.message ?? "Failed to send email.",
          });
          setConfirmationOpen(false);
        },
      }
    );
  };

  const handleCancelConfirmation = () => {
    setConfirmationOpen(false);
    setPendingValues(null);
  };

  return (
    <>
      <SendEmailConfirmationModal
        open={confirmationOpen}
        recipients={confirmationRecipients}
        subject={confirmationSubject}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelConfirmation}
        loading={mutation.isPending}
      />
      <Form
        form={form}
        layout="vertical"
        className="bg-white border rounded-lg p-4"
        onFinish={onSubmit}
        initialValues={{
          channels: PreferredCommunicationMethod.EMAIL,
          subject: booking?.referenceNumber
            ? `AHC ${booking.referenceNumber} - Booking Communication`
            : 'AHC - Booking Communication',
        }}
      >
      <h3 className="text-lg font-medium mb-3">Send Email</h3>

      <div className="space-y-4">
        {/* Recipients Row: TO, CC, BCC */}
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="recipients"
              label={<span className="font-semibold">TO</span>}
              className="mb-0"
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value || (
                      (!value.recipientTypes || value.recipientTypes.length === 0) &&
                      (!value.recipientIds || value.recipientIds.length === 0) &&
                      (!value.additionalEmails || value.additionalEmails.length === 0)
                    )) {
                      throw new Error('Please select at least one recipient');
                    }
                  },
                },
              ]}
            >
              <RecipientSelector />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="ccRecipients"
              label={<span className="font-semibold">CC</span>}
              className="mb-0"
            >
              <RecipientSelector />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="bccRecipients"
              label={<span className="font-semibold">BCC</span>}
              className="mb-0"
            >
              <RecipientSelector />
            </Form.Item>
          </Col>
        </Row>

        {/* Template (Optional) */}
        <Form.Item name="templateId" label="Template (Auto-fills)" className="mb-0">
          <Select
            loading={isLoading}
            placeholder="Select template (optional)"
            allowClear
            onChange={handleTemplateChange}
            options={templates?.items.map((temp) => ({
              label: capitalize(temp.name),
              value: temp.id,
            }))}
          />
        </Form.Item>

        {/* Subject */}
        <Form.Item
          name="subject"
          label="Subject"
          className="mb-0"
          rules={[{ required: true, message: 'Please enter a subject' }]}
        >
          <Input type="text" placeholder="Enter subject..." />
        </Form.Item>

        {/* Message Body */}
        <Form.Item
          name="body"
          label="Message"
          className="mb-0"
          rules={[{ required: true, message: 'Please enter a message' }]}
        >
          <Input.TextArea rows={8} placeholder="Type your message here..." />
        </Form.Item>

        <div className="flex justify-end space-x-2">
          <Button type="default" onClick={() => form.resetFields()}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Send Email
          </Button>
        </div>
      </div>
    </Form>
    </>
  );
};

